// Generator configuration types for dynamic form rendering

export type JurisdictionLevel = 'state' | 'country' | 'none';
export type AnalysisDomain = 'safety' | 'quality' | 'operational' | 'environmental' | 'competency' | 'management' | 'none';

// UI labels that can be customized per generator
export interface GeneratorUILabels {
  // Processing modal - Analysis section
  analysis_heading?: string;
  scanning_label?: string;
  scanning_message?: string;
  identified_label?: string;
  none_detected_message?: string;
  
  // Processing modal - Standards section
  standards_section_heading?: string;
  standards_checking_message?: string;
  standards_preparing_message?: string;
  
  // Processing steps
  step_detecting?: string;
  step_processing?: string;
  step_searching?: string;
  
  // Toast messages
  cancel_toast_message?: string;
}

// Icons available for attachment category rows. Maps to lucide-react icons in ChatInterface.
export type AttachmentCategoryIcon =
  | 'ImagePlus'
  | 'Camera'
  | 'FileStack'
  | 'FileText'
  | 'ClipboardList'
  | 'BookOpen'
  | 'Wrench';

/**
 * A single row shown in the attachment popover. Each category targets one of the two
 * underlying MIME groups (`photo` = images, `plan` = PDFs) but exposes per-generator
 * copy so e.g. ITP can say "Attach inspection reports" while SOP says "Attach equipment manuals".
 */
export interface AttachmentCategory {
  /** Stable identifier sent up with the file for analytics + downstream prompts */
  key: string;
  /**
   * Underlying file group — drives accepted file types, backend validation,
   * and (in future) per-kind extraction prompts.
   *  - `photo`: images (JPG, PNG, HEIC, WebP)
   *  - `plan`:  PDF drawings/schematics
   *  - `doc`:   PDF / DOCX / XLSX project documents
   */
  kind: 'photo' | 'plan' | 'doc';
  /** Primary row label (e.g. "Attach inspection reports") */
  label: string;
  /** Optional helper line shown under the label */
  helper?: string;
  /** Icon name (resolved client-side); defaults by `kind` if omitted */
  icon?: AttachmentCategoryIcon;
}

// Per-generator attachments configuration (drives ChatInterface CTA + api-generate validation)
export interface AttachmentConfig {
  enabled: boolean;
  cta_label: string;
  cta_short_label: string;
  helper_text: string;
  suggested_categories: string[];
  /**
   * Combined cap across all kinds (photo + plan + doc). Acts as a hard ceiling
   * regardless of per-kind limits.
   */
  max_files: number;
  max_file_mb: number;
  /** Per-kind ceilings. Defaults: 10 photos, 5 drawings, 5 docs. */
  max_photos?: number;
  max_plans?: number;
  max_docs?: number;
  /**
   * Optional per-row customisation. When present + non-empty, ChatInterface renders
   * one button per category instead of the default Photos / Plans pair. Empty / omitted
   * preserves legacy SWMS behaviour for backward compatibility.
   */
  categories?: AttachmentCategory[];
}

export const DEFAULT_ATTACHMENT_CONFIG: AttachmentConfig = {
  enabled: false,
  cta_label: 'Attach files',
  cta_short_label: 'Attach',
  helper_text: 'Add supporting files to give the AI more context.',
  suggested_categories: [],
  max_files: 20,
  max_file_mb: 10,
  max_photos: 10,
  max_plans: 5,
  max_docs: 5,
  categories: [],
};

// Standards behavior configuration
export interface StandardsBehavior {
  requires_standards_check?: boolean;
  requires_activity_analysis?: boolean;
  fallback_to_international?: boolean;
}

// Default UI labels (SWMS-based for backward compatibility)
export const DEFAULT_UI_LABELS: Required<GeneratorUILabels> = {
  analysis_heading: 'Hazard Analysis',
  scanning_label: 'Identifying Hazard Keywords',
  scanning_message: 'Scanning prompt for hazard keywords...',
  identified_label: 'Hazards',
  none_detected_message: 'No specific hazards detected',
  standards_section_heading: 'Regulatory Standards Analysis',
  standards_checking_message: 'Checking sources...',
  standards_preparing_message: 'Preparing to check regulatory sources...',
  step_detecting: 'Detecting hazards',
  step_processing: 'Processing controls',
  step_searching: 'Searching standards',
  cancel_toast_message: 'Your document generation has been stopped.',
};

export interface GeneratorConfig {
  id: string;
  slug: string;
  acronym: string;
  display_name: string;
  full_name: string;
  jurisdiction_level: JurisdictionLevel;
  requires_project_reference: boolean;
  requires_standard_reference: boolean;
  requires_contract_reference: boolean;
  supported_countries: string[];
  supported_export_formats: string[];
  placeholder: string;
  hero_heading: string;
  hero_subheading: string;
  disclaimer: string;
  example_prompts: string[];
  // SEO metadata
  meta_title?: string;
  meta_description?: string;
  og_image_path?: string;
  keywords?: string[];
  structured_data?: Record<string, unknown>;
  favicon_path?: string;
  // Dynamic UI labels
  ui_labels?: GeneratorUILabels;
  // Standards behavior configuration
  standards_behavior?: StandardsBehavior;
  // Routing configuration - determines which edge functions to use
  use_generic_pipeline?: boolean;
  // Analysis domain - determines which category types to use for activity analysis
  analysis_domain?: AnalysisDomain;
  // About modal content (SEO/AEO optimized, stored per-generator)
  about_what_is?: string;
  about_how_it_works?: string;
  // Success modal marketing banner (optional, per-generator)
  success_marketing_heading1?: string;
  success_marketing_heading2?: string;
  success_marketing_copy?: string;
  success_marketing_image?: string;
  success_cta_label?: string;
  success_cta_url?: string;
  // Primary domain for display (from generator_domains)
  primary_domain?: string;
  // Associated product code (e.g. DP, GB)
  product_code?: string;
  // Per-generator attachments UX + limits
  attachment_config?: AttachmentConfig;
  // Per-generator prompt length limit (from generator_validation_rules)
  max_prompt_length?: number;
}

// Context data collected based on generator requirements
export interface ContextData {
  // Jurisdiction fields (for jurisdiction_level: 'state' or 'country')
  country?: string;
  stateProvince?: string;
  
  // Project reference fields (for requires_project_reference: true)
  projectName?: string;
  projectNumber?: string;
  
  // Standard reference fields (for requires_standard_reference: true)
  standardReference?: string;
  specificationSection?: string;
  
  // Contract reference fields (for requires_contract_reference: true)
  contractReference?: string;
  contractNumber?: string;
}

// Combined form data for any generator
export interface GeneratorFormData {
  // Common fields
  activity: string;
  methodDescription?: string;
  
  // Context data (varies by generator)
  context: ContextData;
  
  // Generator identifier
  generatorSlug: string;
}

// Helper to determine what context inputs are needed
export function getRequiredContextFields(config: GeneratorConfig): {
  needsJurisdiction: boolean;
  needsState: boolean;
  needsProject: boolean;
  needsStandard: boolean;
  needsContract: boolean;
} {
  return {
    needsJurisdiction: config.jurisdiction_level !== 'none',
    needsState: config.jurisdiction_level === 'state',
    needsProject: config.requires_project_reference,
    needsStandard: config.requires_standard_reference,
    needsContract: config.requires_contract_reference,
  };
}

// Default generator config (SWMS) for backward compatibility
export const DEFAULT_GENERATOR_CONFIG: GeneratorConfig = {
  id: 'ef0acee6-2c17-4043-b84c-56c6a3fd0e9a',
  slug: 'swms',
  acronym: 'SWMS',
  display_name: 'SWMS',
  full_name: 'Safe Work Method Statement',
  jurisdiction_level: 'state',
  requires_project_reference: false,
  requires_standard_reference: false,
  requires_contract_reference: false,
  supported_countries: ['Australia', 'New Zealand'],
  supported_export_formats: ['docx'],
  placeholder: 'Describe the work activity...',
  hero_heading: 'SWMS Generator',
  hero_subheading: 'Generate Safe Work Method Statements (SWMS) in seconds - free and unlimited.',
  disclaimer: 'SWMS Generator can make mistakes. Check draft documents before use.',
  example_prompts: [
    'Operating a 6m electric scissor lift indoors to install overhead lighting in a warehouse.',
    'Excavating a 1.2m deep trench for laying new electrical conduits along a footpath.',
    'Using a 125mm angle grinder to cut 50x50 SHS steel sections in a fabrication workshop.',
  ],
  // SEO defaults
  meta_title: 'SWMS Generator - Free Safe Work Method Statements',
  meta_description: 'Generate professional Safe Work Method Statements (SWMS) in seconds. Free, unlimited, and compliant with Australian workplace safety requirements.',
  keywords: ['SWMS', 'Safe Work Method Statement', 'workplace safety', 'risk assessment', 'Australia', 'construction safety'],
  favicon_path: '/favicon.png',
  // UI labels
  ui_labels: DEFAULT_UI_LABELS,
};

// Validate that required context fields are filled based on generator config
export function validateContextData(
  context: ContextData,
  config: GeneratorConfig
): { valid: boolean; missingFields: string[] } {
  const missingFields: string[] = [];

  if (config.jurisdiction_level !== 'none' && !context.country) {
    missingFields.push('country');
  }

  if (config.requires_project_reference && !context.projectName?.trim()) {
    missingFields.push('projectName');
  }

  if (config.requires_standard_reference && !context.standardReference?.trim()) {
    missingFields.push('standardReference');
  }

  if (config.requires_contract_reference && !context.contractReference?.trim()) {
    missingFields.push('contractReference');
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
